import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import StepWrapper from '../stepWrapper';
import {
  OrderFlowTabs,
  setTravelDetails
} from '../../../redux/orderFlow/orderFlowSlice';
import { NewFlowBox } from './PersonalDetails';
import { SelectInput } from '../../atoms/select-input/select-input';
import { DatePicker } from '../../atoms/date-picker/date-picker';
import {
  validateDepartureDate,
  validateReturnDate
} from '../../../utils/form-validator';
import {
  getTomorrowDate,
  getMaxReturnDate,
  getMaxDepartureDate,
  getMinReturnDate,
  deserializeDate,
  serializeDate,
  MINIMUM_DAYS_FROM_DEPARTURE
} from '../../atoms/date-picker/date-picker-functions';
import { addDays } from 'date-fns';
import Tooltip from '../../atoms/tooltip/tooltip';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '../../../utils/i18n-utils';
import { navigateToNextTab } from '../../../utils/navigation-helpers';
import { fetchCountries } from '../../../redux/api-v2';
import {
  getOriginCountriesFromApi,
  getDestinationCountriesForOrigin,
  getAllDestinationCountriesFromApi,
  getCountryOptions,
  isValidOriginCountry,
  isValidDestinationCountry
} from '../../../utils/countries-adapter';
import { CountryCode } from '@oncampusabroad/api';

const TravelDetails = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const language = getLanguage();
  const [destinations, setDestinations] = useState<string>(
    useAppSelector(state => state.orderFlow.destinationCountryCode) || ''
  );
  const [home, setHome] = useState<string>(
    useAppSelector(state => state.orderFlow.homeCountryCode) || ''
  );
  const [departureDate, setDepartureDate] = useState<string>(
    useAppSelector(state => state.orderFlow.departureDate) || getTomorrowDate()
  );
  const initialDepartureDate =
    useAppSelector(state => state.orderFlow.departureDate) || getTomorrowDate();
  const savedReturnDate = useAppSelector(state => state.orderFlow.returnDate);

  const [returnDate, setReturnDate] = useState<string>(() => {
    if (savedReturnDate) return savedReturnDate;

    const departure = deserializeDate(initialDepartureDate);
    const minimumReturnDate = addDays(
      departure,
      MINIMUM_DAYS_FROM_DEPARTURE - 1
    );
    return serializeDate(minimumReturnDate);
  });
  const [homeCountryError, setHomeCountryError] = useState(false);
  const [destinationCountryError, setDestinationCountryError] = useState(false);
  const [departureDateError, setDepartureDateError] = useState('');
  const [returnDateError, setReturnDateError] = useState('');
  const [sameCountryError, setSameCountryError] = useState(false);

  const [originCountries, setOriginCountries] = useState<any[]>([]);
  const [destinationCountries, setDestinationCountries] = useState<any[]>([]);

  const { loading: countriesLoadingState, response: countriesResponse } =
    useAppSelector(state => state.countries.countries);
  const countriesAreLoading =
    countriesLoadingState === 'pending' || countriesLoadingState === 'idle';
  const countriesAreLoaded =
    countriesLoadingState === 'succeeded' &&
    countriesResponse &&
    countriesResponse.length > 0;

  useEffect(() => {
    const countries = getOriginCountriesFromApi();
    const options = getCountryOptions(language, countries);
    setOriginCountries(options);
  }, [language, countriesLoadingState]);

  useEffect(() => {
    let countries;
    if (home) {
      countries = getDestinationCountriesForOrigin(home);
    } else {
      countries = getAllDestinationCountriesFromApi();
    }
    const options = getCountryOptions(language, countries);
    setDestinationCountries(options);
  }, [home, language, countriesLoadingState]);

  const getMinimumValidReturnDate = (departureDate: string) => {
    if (!departureDate) return '';
    const departure = deserializeDate(departureDate);
    const minimumReturnDate = addDays(
      departure,
      MINIMUM_DAYS_FROM_DEPARTURE - 1
    );
    return serializeDate(minimumReturnDate);
  };

  useEffect(() => {
    if (departureDate && !savedReturnDate) {
      const newReturnDate = getMinimumValidReturnDate(departureDate);
      setReturnDate(newReturnDate);
    }
  }, [departureDate, savedReturnDate]);

  const handleHomeCountryChange = (value: string) => {
    setHome(value);
    if (destinations && !isValidDestinationCountry(destinations, value)) {
      setDestinations('');
    }
  };

  const validate = () => {
    const homeCountryIsValid = isValidOriginCountry(home);
    const destinationCountryIsValid = home
      ? isValidDestinationCountry(destinations, home)
      : false;

    const departureDateIsValid = validateDepartureDate(
      returnDate,
      departureDate
    );
    const returnDateIsValid = validateReturnDate(returnDate, departureDate);
    const sameCountry = home === destinations;
    setSameCountryError(sameCountry);
    setHomeCountryError(!homeCountryIsValid);
    setDestinationCountryError(!destinationCountryIsValid);
    setDepartureDateError(departureDateIsValid);
    setReturnDateError(returnDateIsValid);
    return (
      homeCountryIsValid &&
      destinationCountryIsValid &&
      departureDateIsValid.length === 0 &&
      returnDateIsValid.length === 0 &&
      !sameCountry
    );
  };

  const handleContinue = async () => {
    const isValid = validate();
    if (isValid) {
      dispatch(
        setTravelDetails({
          departureDate,
          returnDate,
          homeCountryCode: home as CountryCode,
          destinationCountryCode: destinations as CountryCode
        })
      );
      navigateToNextTab(dispatch, OrderFlowTabs.IdentificationDetails);
    }
  };
  const handleBack = () => {
    navigateToNextTab(dispatch, OrderFlowTabs.PersonalDetails);
  };

  const renderContent = () => {
    if (countriesAreLoading && originCountries.length === 0) {
      return (
        <div
          className='loading-container'
          style={{ textAlign: 'center', padding: '20px' }}
        >
          <p>{t('loading-countries-data')}</p>
        </div>
      );
    }

    if (
      !countriesAreLoading &&
      !countriesAreLoaded &&
      originCountries.length === 0
    ) {
      return (
        <div
          className='error-container'
          style={{ textAlign: 'center', padding: '20px' }}
        >
          <p>{t('error-loading-countries')}</p>
          <button
            onClick={() => dispatch(fetchCountries())}
            style={{ margin: '10px', padding: '5px 10px' }}
          >
            {t('try-again')}
          </button>
        </div>
      );
    }

    return (
      <NewFlowBox>
        <Tooltip
          children={
            <SelectInput
              field={{
                id: 'homeCountry',
                type: 'country',
                mandatory: true
              }}
              options={originCountries}
              onChange={handleHomeCountryChange}
              value={home}
              warning={homeCountryError}
            />
          }
          tooltipText={t('tooltip-country')}
        />
        <SelectInput
          field={{
            id: 'destinationCountry',
            type: 'country',
            mandatory: true
          }}
          options={destinationCountries}
          onChange={setDestinations}
          value={destinations}
          warning={destinationCountryError || sameCountryError}
        />
        <DatePicker
          minDate={{ enabled: true, date: getTomorrowDate() }}
          maxDate={{ enabled: true, date: getMaxDepartureDate() }}
          field={{
            id: 'departureDate',
            type: 'future-date',
            mandatory: true
          }}
          onChange={setDepartureDate}
          value={departureDate}
          warning={departureDateError}
        />
        <DatePicker
          minDate={{
            enabled: true,
            date: getMinReturnDate(returnDate, departureDate)
          }}
          maxDate={{ enabled: true, date: getMaxReturnDate(departureDate) }}
          field={{
            id: 'returnDate',
            type: 'future-date',
            mandatory: true
          }}
          onChange={setReturnDate}
          value={returnDate}
          warning={returnDateError}
        />
      </NewFlowBox>
    );
  };

  return (
    <StepWrapper
      next={{ f: handleContinue, label: 'NEXT' }}
      back={{ f: handleBack, label: 'BACK' }}
    >
      {renderContent()}
    </StepWrapper>
  );
};

export default TravelDetails;
